import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { MenuItemLink, getResources } from 'react-admin';
import { List, ListItem, ListItemText, ListItemIcon, Divider, Box, Collapse } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import DefaultIcon from '@mui/icons-material/ViewList';
import BarChartIcon from '@mui/icons-material/BarChart';
import RouterIcon from '@mui/icons-material/Router';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import WristbandIcon from '@mui/icons-material/AllInbox';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import config from '../../config';
import { translate } from '../../i18n/customI18nProvider';
import { getName, getRight } from '../../services/storage';
import { EventIcon } from './../../forms/Events';
import { ConsumptionIcon } from '../../forms/AllConsumptions';
import { VoucherIcon } from '../../forms/Vouchers';
import { EntranceIcon } from '../../forms/Entrances';
import { ClientIcon } from '../../forms/clients/ClientsList';
import { UserIcon } from '../../forms/Users';
import { ZoneIcon } from '../../forms/Zones';
import { RefundIcon } from '../../forms/Refunds';
import { ReportIcon } from '../../forms/Reports';

const MenuCollapse = ({ title, icon, items, resourcesByName, onMenuClick, openSidebar }) => {
    const location = useLocation();

    const isSubItemActive = useCallback(() => {
        return items.some((subItem) => {
            if (subItem.externalLink) return false;
            const resource = resourcesByName[subItem.name];
            const routeName = resource ? resource.name : subItem.name;
            return location.pathname.startsWith(`/${routeName}`);
        });
    }, [items, location.pathname, resourcesByName]);

    const [open, setOpen] = useState(isSubItemActive);

    useEffect(() => {
        if (isSubItemActive()) {
            setOpen(true);
        }
    }, [location.pathname, isSubItemActive]);

    const handleClick = () => {
        setOpen(!open);
    };

    return (
        <>
            <ListItem button onClick={handleClick}>
                <ListItemIcon style={{ minWidth: '40px', color: 'action' }}>
                    {icon || <DefaultIcon />}
                </ListItemIcon>
                <ListItemText primary={title} />
                {open ? <ExpandLess style={{ color: 'action' }} /> : <ExpandMore style={{ color: 'action' }} />}
            </ListItem>

            <Collapse in={open} timeout="auto" unmountOnExit>
                <List component="div" disablePadding style={{ paddingLeft: '8px' }}>
                    {items.map((subItem, index) => {
                        if (subItem.externalLink) {
                            return (
                                <MenuItemLink
                                    key={subItem.name || `ext-${index}`}
                                    component="a"
                                    href={subItem.externalLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    primaryText={translate(`custom.${subItem.name}`) || subItem.name}
                                    leftIcon={subItem.icon || <DefaultIcon />}
                                    onClick={onMenuClick}
                                    sidebarIsOpen={openSidebar}
                                />
                            );
                        }

                        const resource = resourcesByName[subItem.name];
                        if (!resource) return null;

                        return (
                            <MenuItemLink
                                key={resource.name}
                                to={`/${resource.name}`}
                                primaryText={resource.options?.label || resource.name}
                                leftIcon={resource.icon ? <resource.icon /> : <DefaultIcon />}
                                onClick={onMenuClick}
                                sidebarIsOpen={openSidebar}
                            />
                        );
                    })}
                </List>
            </Collapse>
        </>
    );
};

const Menu = ({ onMenuClick, logout }) => {
    const open = useSelector(state => state.admin.ui.sidebarOpen);
    const rawResources = useSelector(getResources);

    const resourcesByName = React.useMemo(() => {
        return rawResources.reduce((acc, resource) => {
            acc[resource.name] = resource;
            return acc;
        }, {});
    }, [rawResources]);

    const name = getName();
    const right = getRight();

    const menuSuperadmin = [
        { type: 'divider' },
        { type: 'resource', name: 'events' },
        { type: 'resource', name: 'users' },
        { type: 'resource', name: 'v1/clients' },
        { type: 'resource', name: 'v1/payments' },
        { type: 'resource', name: 'v1/tickets' },

        { type: 'divider' },
        { type: 'resource', name: 'v1/pollWaitList' },
        { type: 'resource', name: 'v1/mypos_direct_transactions' },

        { type: 'divider' },
        { type: 'resource', name: 'replica_sets' },
        { type: 'resource', name: 'latest_app' },

        { type: 'divider' },
        {
            type: 'custom',
            render: () => (
                <>
                    <MenuItemLink
                        component="a"
                        href="/v2/admin/devices"
                        primaryText={translate('custom.devices')}
                        leftIcon={<RouterIcon />}
                        onClick={onMenuClick}
                        sidebarIsOpen={open}
                    />
                    <MenuItemLink
                        component="a"
                        href="/v2/admin/mypos_terminals"
                        primaryText={translate('custom.mypos_terminals')}
                        leftIcon={<PointOfSaleIcon />}
                        onClick={onMenuClick}
                        sidebarIsOpen={open}
                    />
                    <MenuItemLink
                        component="a"
                        href="/v2/admin/wristbands"
                        primaryText={translate('custom.wristbands')}
                        leftIcon={<WristbandIcon />}
                        onClick={onMenuClick}
                        sidebarIsOpen={open}
                    />
                </>
            )
        }
    ];

    const menuAdmin = [
        { type: 'divider' },
        {
            type: 'custom',
            render: () => (
                <>
                    <MenuItemLink
                        component="a"
                        href="/statistics"
                        primaryText={translate('custom.statistics')}
                        leftIcon={<BarChartIcon />}
                        onClick={onMenuClick}
                        sidebarIsOpen={open}
                    />
                    <MenuItemLink
                        to="/events"
                        primaryText={translate('custom.parameters')}
                        leftIcon={<EventIcon />}
                        onClick={onMenuClick}
                        sidebarIsOpen={open}
                    />
                </>
            )
        },

        { type: 'divider' },
        { type: 'resource', name: 'deposits' },
        { type: 'resource', name: 'bars' },
        { type: 'resource', name: 'all_consumptions' },

        { type: 'divider' },
        {
            type: 'collapse',
            title: translate('custom.vouchers'),
            icon: <VoucherIcon />,
            items: [
                { name: 'vouchers' },
                { name: 'voucher_packs' },
            ]
        },

        { type: 'divider' },
        {
            type: 'collapse',
            title: translate('custom.entrances'),
            icon: <EntranceIcon />,
            items: [
                { name: 'entrances' },
                { name: 'entrance_groups' },
            ]
        },

        { type: 'divider' },
        {
            type: 'collapse',
            title: translate('custom.clients'),
            icon: <ClientIcon />,
            items: [
                { name: 'v1/clients' },
                { name: 'v1/payments' },
                { name: 'feedbacks' },
                { name: 'suspicious_clients' },
            ]
        },

        { type: 'divider' },
        {
            type: 'collapse',
            title: translate('custom.users'),
            icon: <UserIcon />,
            items: [
                { name: 'users' },
                { name: 'smartphones' },
                { name: 'mypos' },
            ]
        },

        { type: 'divider' },
        {
            type: 'collapse',
            title: translate('custom.ticketing'),
            icon: <QrCode2Icon />,
            items: [
                { name: 'v1/tickets' },
                { name: 'infomaniak', externalLink: 'https://manager.infomaniak.com/', icon: <LocalActivityIcon /> },
                { name: 'zones' },
            ]
        },

        { type: 'divider' },
        {
            type: 'collapse',
            title: translate('custom.refunds'),
            icon: <RefundIcon />,
            items: [
                { name: 'v1/refunds' },
                { name: 'v1/events/refund_errors' },
            ]
        },

        { type: 'divider' },
        {
            type: 'collapse',
            title: translate('custom.payments'),
            icon: <ReportIcon />,
            items: [
                { name: 'v1/pollWaitList' },
                { name: 'v1/mypos_direct_transactions' },
                { name: 'reports' },
            ]
        },
    ];

    const menuBar = [
        { type: 'divider' },
        {
            type: 'custom',
            render: () => (
                <>
                    <MenuItemLink
                        component="a"
                        href="/statistics"
                        primaryText={translate('custom.statistics')}
                        leftIcon={<BarChartIcon />}
                        onClick={onMenuClick}
                        sidebarIsOpen={open}
                    />
                </>
            )
        },

        { type: 'divider' },
        { type: 'resource', name: 'bars' },
        { type: 'resource', name: 'users' },
    ];

    const menu = right === config.permissions.SUPERADMIN.value ? menuSuperadmin : right === config.permissions.ADMIN.value ? menuAdmin : menuBar;

    return (
        <List style={{ width: 220 }}>
            <ListItem>
                <ListItemText
                    primary="JM Contactless"
                    secondary={right !== config.permissions.SUPERADMIN.value ? name : ''}
                />
            </ListItem>

            {menu.map((item, index) => {
                switch (item.type) {
                    case 'divider':
                        return <Divider 
                            key={`divider-${index}`}
                            style={{ backgroundColor: '#495057' }} />;

                    case 'collapse':
                        return (
                            <MenuCollapse
                                key={`collapse-${index}`}
                                title={item.title}
                                icon={item.icon}
                                items={item.items}
                                resourcesByName={resourcesByName}
                                onMenuClick={onMenuClick}
                                openSidebar={open}
                            />
                        );

                    case 'resource': {
                        const resource = resourcesByName[item.name];
                        if (!resource) return null;

                        return (
                            <MenuItemLink
                                key={resource.name}
                                to={`/${resource.name}`}
                                primaryText={resource.options?.label || resource.name}
                                leftIcon={resource.icon ? <resource.icon /> : <DefaultIcon />}
                                onClick={onMenuClick}
                                sidebarIsOpen={open}
                            />
                        );
                    }

                    case 'custom':
                        return <React.Fragment key={`custom-${index}`}>{item.render()}</React.Fragment>;

                    default:
                        return null;
                }
            })}

            <Divider style={{ backgroundColor: '#495057' }} />
            {logout}
            <Divider style={{ backgroundColor: '#495057' }} />
            <Box style={{ textAlign: 'center' }}>
                <small>{config.version}</small>
            </Box>
        </List>
    );
};

export default Menu;